import React, { useCallback, useContext, useState } from 'react';
import { connect } from 'react-redux';
import CloseIcon from 'mdi-react/CloseIcon';

import { AppContext } from '@prisjakt/ui-app-context';
import { MarketCodes } from '@prisjakt/web-app-config/types/market';

import { postFeedback as feedbackRequest } from '../../../../globals/api/requests';
import ReportModal from './report-modal';

import { reportWrongPrice as reportWrongPriceAction } from '../../../../areas/feedback/actions';

import { StockStatus, Price } from '../../../../types';

import * as S from './styles';

type Metadata = {
  message: string;
  ['agent_type_key']: ReportType;
  uri: string;
  ['country_code']: MarketCodes;
  ['store_id']: number;
  ['price_id']: number;
  ['price_value']: string;
  ['stock_status']: string;
};

enum ReportType {
  WrongPrice = 'price',
  WrongStockStatus = 'stock',
  WrongProduct = 'wrong_product',
}

type OwnProps = {
  onClose: () => void;
  priceValue?: number | null;
  priceStore: Price['store'];
  productImage?: string | null;
  priceId?: string | null;
  productName?: string;
  reportWrongPriceDispatch: (metadata: Metadata) => {};
  stockStatus?: StockStatus | null;
};

const ReportButton = ({
  onClose,
  priceValue,
  priceStore,
  productImage,
  reportWrongPriceDispatch,
  priceId,
  productName,
  stockStatus,
}: OwnProps) => {
  const { market, t } = useContext(AppContext);

  const [success, setSuccess] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const handleOnClose = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      onClose();
    },
    [onClose]
  );

  const postFeedback = async (data: Metadata) => {
    const res = await feedbackRequest(data);

    if (res.data.feedback.success === true) {
      return res;
    }
    if (res.errors) {
      throw new Error(
        `Error creating report on price: ${JSON.stringify(res.errors)}`
      );
    }

    throw new Error('Error creating report on price.');
  };

  const onReport = useCallback(
    (e: React.MouseEvent, type: ReportType) => {
      if (!IS_BROWSER) return;

      // Stops click out action event
      e.preventDefault();
      e.stopPropagation();

      const message = `Error report on ${window.location.href}`;

      const metadata = {
        message,
        agent_type_key: type,
        uri: window.location.href,
        country_code: market.code,
        store_id: priceStore && priceStore.id,
        price_id: parseInt(priceId as string, 10),
        price_value: String(priceValue),
        stock_status: stockStatus,
      } as Metadata;

      reportWrongPriceDispatch(metadata); // Action event for tracking

      setSending(true);
      postFeedback(metadata)
        .then(() => {
          setSending(false);
          setSuccess(true);
        })
        .catch(() => {
          setSending(false);
          setError(true);
        })
        .then(() => {
          setTimeout(() => {
            onClose();
          }, 3000);
        });
    },
    [
      market.code,
      onClose,
      priceId,
      priceStore,
      priceValue,
      reportWrongPriceDispatch,
      stockStatus,
    ]
  );

  const reportWrongPrice = useCallback(
    (e: React.MouseEvent) => onReport(e, ReportType.WrongPrice),
    [onReport]
  );
  const reportWrongProduct = useCallback(
    (e: React.MouseEvent) => onReport(e, ReportType.WrongProduct),
    [onReport]
  );
  const reportWrongStockStatus = useCallback(
    (e: React.MouseEvent) => onReport(e, ReportType.WrongStockStatus),
    [onReport]
  );

  const closeIcon = <CloseIcon />;

  const openModal = useCallback(
    (e: React.MouseEvent) => {
      // Stops the PriceRow link and onClick from firing
      e.preventDefault();
      e.stopPropagation();

      setModalIsOpen(true);
    },
    [setModalIsOpen]
  );

  const closeModal = useCallback(() => setModalIsOpen(false), [setModalIsOpen]);

  return (
    <>
      <ReportModal
        isOpen={modalIsOpen}
        error={error}
        closeModal={closeModal}
        priceValue={priceValue}
        priceStore={priceStore}
        productImage={productImage}
        productName={productName}
        reportWrongPrice={reportWrongPrice}
        reportWrongProduct={reportWrongProduct}
        reportWrongStockStatus={reportWrongStockStatus}
        sending={sending}
        stockStatus={stockStatus}
        success={success}
      />

      <S.ReportOverlay>
        <S.PromptTitle>
          {t('feedback', 'dialog.prompt-title')}

          <S.ToggleButtons type="button" onClick={openModal}>
            {t('feedback', 'dialog.prompt-button')}
          </S.ToggleButtons>
        </S.PromptTitle>

        <S.CloseButton
          variant="icon"
          icon={closeIcon}
          onClick={handleOnClose}
        />
      </S.ReportOverlay>
    </>
  );
};

const dispatchProps = {
  reportWrongPriceDispatch: reportWrongPriceAction,
};

export default connect(null, dispatchProps)(ReportButton);
